import React, { useState } from 'react';
import style from '../content-type.module.scss';
import { AdaptiveCard as TAdaptiveCard  } from '../../types';
import moment from 'moment';
import { EditIcon, DeleteIcon } from '../../../../icons';
import { useMutate, useAppAndBranchId, usePermissions } from '../../../../hooks';
import { api } from '../../api';
import { Modal } from '../../../../components';
import { AddAdaptiveCard } from './AddAdaptiveCard';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import { errorMessage } from '../../../../utils/error';

type Props = {
	card: TAdaptiveCard & { id: string; updatedAt: string|null; createdAt: string };
	refetchContent: () => void;
};

export function AdaptiveCard(props: Props) {
  const { ErrorToast } = useToast()
	const [appId, branchId] = useAppAndBranchId();
	const { card, refetchContent } = props;
	const { mutate: deleteMutate, loading: deleteLoading } = useMutate(
		api.deleteContent,
	);

	const [modalVisibility, setModalVisibility] = useState({
		editContent: false,
		viewContent: false,
		deleteContent: false,
	});

	const [canEdit, canDelete] = usePermissions(['content.Edit', 'content.Delete']) as boolean[]

	function updateVisibility(
		key: keyof typeof modalVisibility,
		visibility: boolean,
	) {
		setModalVisibility((prev) => ({
			...prev,
			[key]: visibility,
		}));
	}

	return (
		<div className={style.contentTypeWrapper}>
			<div data-has-action={canEdit || canDelete} className={style.contentType} onClick={() => {
					updateVisibility('viewContent', true)
				}}>
				<div className={style.left}>
					<p>{card.contentValue.title}</p>
				</div>
				<div className={style.info}>
					<p>{card?.updatedAt ? moment(card?.updatedAt).format('DD MMM YYYY, hh:mm A') : "" }</p>
				</div>
				<div className={style.info}>
					<p>{moment(card.createdAt).format('DD MMM YYYY, hh:mm A')}</p>
					<div className={style.actions}>
						{(canEdit || canDelete) && <div className={style.line} />}
						{canEdit && <button
							onClick={(e) => {
								e.stopPropagation()
								updateVisibility('editContent', true);
							}}
						>
							<EditIcon width="14" height="14" stroke="#141B34" />
						</button>
						}{canDelete &&
							<button
								onClick={async (e) => {
									e.stopPropagation()
									updateVisibility('deleteContent', true);
								}}
							>
								<DeleteIcon width="14" height="14" stroke="#D30000" />
							</button>
						}
					</div>
				</div>
			</div>
			{modalVisibility.viewContent && (
				<AddAdaptiveCard
					mode="view"
					refetchContent={refetchContent}
					initialValue={{
						title: card.contentValue.title,
						payload: card.contentValue.payload,
						id: card.id,
					}}
					isOpen={modalVisibility.viewContent}
					close={() => {
						updateVisibility('viewContent', false);
					}}
				/>
			)}
			{modalVisibility.editContent && (
				<AddAdaptiveCard
					mode="edit"
					refetchContent={refetchContent}
					initialValue={{
						title: card.contentValue.title,
						payload: card.contentValue.payload,
						id: card.id,
					}}
					isOpen={modalVisibility.editContent}
					close={() => updateVisibility('editContent', false)}
				/>
			)}
			<Modal
				styles={{
					'--ok-btn-bg': '#DE0000',
					'--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
				}}
				okText="Delete"
				loading={deleteLoading}
				onOk={async () => {
				  try {
						await deleteMutate(appId, branchId, card.id);
						refetchContent();
					} catch(err) {
					  ErrorToast(errorMessage("delete", "adaptive card", err?.code))
					}
				}}
				onCancel={() => updateVisibility('deleteContent', false)}
				isOpen={modalVisibility.deleteContent}
				title="Remove Chosen Adaptive Card"
			>
				<div
					style={{
						padding: '18px 14px',
						color: '#393939',
						fontSize: 15,
						fontWeight: 500,
					}}
				>
					<p>Do you really want to remove the chosen adaptive card?</p>
				</div>
			</Modal>
		</div>
	);
}
